import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config.js";
import Pagination from "../Pagination/Paginations.js";
import DownloadButtons from "../DownloadButtons/DownloadButtons.js";
import EntriesSelector from "../Pagination/EntriesSelector";
import "../preloading.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const LOAN_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

const LoanReport = () => {
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM
  const yearStart = `${now.getFullYear()}-01`;

  // "department" => Department Wise Loan tab, "employee" => Employee wise Loan tab
  const [reportType, setReportType] = useState("department");

  // ── Department wise filters ──
  const [filters, setFilters] = useState({
    dept_id: "0",
    loan_status: "all",
    action_type: "show",
  });

  // ── Employee wise filters ──
  const [empFilters, setEmpFilters] = useState({
    from_month: yearStart,
    to_month: currentMonth,
    employee: null, // { value: user_id, label: "EMPID - Name" }
    action_type: "show",
  });

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // ─── Fetch departments ───
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/showdepartment", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      let deptData = [];
      if (Array.isArray(res.data)) deptData = res.data;
      else if (res.data.departments) deptData = res.data.departments;
      else if (res.data.data) deptData = res.data.data;

      setDepartments(deptData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load departments");
    }
  };

  // ─── Fetch all employees (for Employee wise dropdown) ───
  const fetchEmployees = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/all_employees", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      const list = res.data?.data?.all_employee || [];
      setEmployees(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load employees");
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
  }, []);

  // ─── Fetch Loan Report ───
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Build request per tab
    let url = API_BASE_URL + "/loan_report";
    let payload = {
      report_type: reportType,
      dept_id: filters.dept_id,
      loan_status: filters.loan_status,
      action_type: filters.action_type,
    };

    if (reportType === "employee") {
      if (!empFilters.employee) {
        toast.error("Please select an employee", { toastId: "loan-report-validation-employee" });
        return;
      }
      url = API_BASE_URL + "/loan_report_employee";
      payload = {
        employee_id: empFilters.employee.value,
        from_month: empFilters.from_month,
        to_month: empFilters.to_month,
        action_type: empFilters.action_type,
      };
    }

    // block repeat clicks while a request is already running
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      setRecords(res.data.records || []);
      setCurrentPage(1);
      toast.success("Loan report loaded successfully!", {
        position: "bottom-right",
        theme: "colored",
        toastId: "loan-report-success",
      });
    } catch (error) {
      console.error(error);
      toast.error("Error fetching loan report", { toastId: "loan-report-error" });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // ─── Search + Pagination ───
  const filteredItems = records.filter((item) =>
    `${item.employee_id}${item.employee_name}${item.department_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredItems.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const filteredData = filteredItems.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Switch tab: clear results so columns/data match the selected mode
  const switchTab = (type) => {
    if (type === reportType) return;
    setReportType(type);
    setRecords([]);
    setSearchTerm("");
  };

  const isEmployeeWise = reportType === "employee";

  return (
    <>
      <div className="container-fluid mt-4">
        <ToastContainer position="bottom-right" theme="colored" />

        {/* 🔹 Tabs: Department Wise / Employee Wise */}
        <div className="mb-3 d-flex flex-wrap" style={{ gap: "10px" }}>
          <button
            type="button"
            className={`btn btn-sm ${reportType === "department" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => switchTab("department")}
          >
            Department Wise Loan
          </button>
          <button
            type="button"
            className={`btn btn-sm ${reportType === "employee" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => switchTab("employee")}
          >
            Employee wise Loan
          </button>
        </div>

        {/* 🔹 Filter Card */}
        <div className="card shadow-sm mb-4">
          <h6 className="card-header d-flex align-items-center" style={{ color: "#1d3a8a", fontWeight: 600 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
              <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
              <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
              <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
            </svg>
            Loan Report
          </h6>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {!isEmployeeWise ? (
                /* ── Department Wise filters ── */
                <div className="row g-3 align-items-end">
                  {/* Department */}
                  <div className="col-md-3">
                    <label htmlFor="dept_id">Department</label>
                    <Select
                      name="dept_id"
                      value={
                        departments
                          .map((dept) => ({
                            value: dept.department_id || dept.id,
                            label: dept.department_name,
                          }))
                          .find((d) => d.value === filters.dept_id) || null
                      }
                      onChange={(selected) =>
                        setFilters((prev) => ({
                          ...prev,
                          dept_id: selected ? selected.value : "0",
                        }))
                      }
                      options={departments.map((dept) => ({
                        value: dept.department_id || dept.id,
                        label: dept.department_name,
                      }))}
                      isClearable
                      placeholder="All Department"
                      className="basic-single"
                    />
                  </div>

                  {/* Loan Status */}
                  <div className="col-md-3">
                    <label htmlFor="loan_status">Loan Status</label>
                    <Select
                      name="loan_status"
                      value={
                        LOAN_STATUS_OPTIONS.find(
                          (o) => o.value === filters.loan_status
                        ) || LOAN_STATUS_OPTIONS[0]
                      }
                      onChange={(selected) =>
                        setFilters((prev) => ({
                          ...prev,
                          loan_status: selected ? selected.value : "all",
                        }))
                      }
                      options={LOAN_STATUS_OPTIONS}
                      isSearchable={false}
                      className="basic-single"
                    />
                  </div>

                  {/* Submit */}
                  <div className="col-md-2">
                    <button type="submit" className="btn btn-primary btn-sm" style={{ height: "38px" }} disabled={loading}>
                      {loading ? "Loading..." : "Get"}
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Employee Wise filters ── */
                <div className="row g-3 align-items-end">
                  {/* From Month */}
                  <div className="col-md-3">
                    <label>From Month</label>
                    <DatePicker
                      selected={
                        empFilters.from_month
                          ? new Date(empFilters.from_month + "-01")
                          : null
                      }
                      onChange={(date) =>
                        setEmpFilters((prev) => ({
                          ...prev,
                          from_month: date
                            ? date.toLocaleDateString("en-CA").slice(0, 7)
                            : "",
                        }))
                      }
                      dateFormat="yyyy-MM"
                      showMonthYearPicker
                      placeholderText="From Month"
                      className="form-control"
                    />
                  </div>

                  {/* To Month */}
                  <div className="col-md-3">
                    <label>To Month</label>
                    <DatePicker
                      selected={
                        empFilters.to_month
                          ? new Date(empFilters.to_month + "-01")
                          : null
                      }
                      onChange={(date) =>
                        setEmpFilters((prev) => ({
                          ...prev,
                          to_month: date
                            ? date.toLocaleDateString("en-CA").slice(0, 7)
                            : "",
                        }))
                      }
                      dateFormat="yyyy-MM"
                      showMonthYearPicker
                      placeholderText="To Month"
                      className="form-control"
                    />
                  </div>

                  {/* Employee */}
                  <div className="col-md-3">
                    <label>Employee</label>
                    <Select
                      options={employees}
                      value={empFilters.employee}
                      onChange={(selected) =>
                        setEmpFilters((prev) => ({ ...prev, employee: selected }))
                      }
                      isClearable
                      placeholder="Search Emp No / Name"
                      className="basic-single"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                      }}
                    />
                  </div>

                  {/* Submit */}
                  <div className="col-md-2">
                    <button type="submit" className="btn btn-primary btn-sm" style={{ height: "38px" }} disabled={loading}>
                      {loading ? "Loading..." : "Get"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* 🔹 Table Section */}
        <div className="card">
          <div className="card-body">
            {/* Controls */}
            <div className="d-flex justify-content-between mb-2 align-items-center">
              <div className="d-flex align-items-center gap-2">
                <DownloadButtons data={filteredItems} filename="Loan_Report" />
              </div>

              <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                <EntriesSelector
                  setEntriesPerPage={setEntriesPerPage}
                  setCurrentPage={setCurrentPage}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search..."
                  style={{ width: "200px" }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="d-flex justify-content-center p-5">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="table-responsive sticky-table-wrap">
                <table className="table table-bordered table-striped text-center sticky-table sticky-head-tint sticky-no-freeze-col">
                  <thead>
                    {isEmployeeWise ? (
                      <tr>
                        <th>Employee ID</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        <th>Loan Amount</th>
                        <th>Deduction Amount</th>
                        <th>Balance Amount</th>
                        <th>Month</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Employee ID</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        <th>Loan Amount</th>
                        <th>Loan Deducted Month</th>
                        <th>Loan Amount Paid</th>
                        <th>Monthly Deduction</th>
                        <th>Loan Balance</th>
                        <th>Loan Status</th>
                        <th>Loan Start Month</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {filteredData.length > 0 ? (
                      filteredData.map((row, idx) =>
                        isEmployeeWise ? (
                          <tr key={idx}>
                            <td>{row.employee_id}</td>
                            <td>{row.employee_name}</td>
                            <td>{row.department_name}</td>
                            <td>{row.loan_amount}</td>
                            <td>{row.deduction_amount}</td>
                            <td>{row.balance_amount}</td>
                            <td>{row.month}</td>
                          </tr>
                        ) : (
                          <tr key={idx}>
                            <td>{row.employee_id}</td>
                            <td>{row.employee_name}</td>
                            <td>{row.department_name}</td>
                            <td>{row.loan_amount}</td>
                            <td>{row.deducted_months}</td>
                            <td>{row.amount_paid}</td>
                            <td>{row.monthly_deduction}</td>
                            <td>{row.loan_balance}</td>
                            <td>
                              <span
                                className={`badge ${
                                  String(row.loan_status).toLowerCase() === "closed"
                                    ? "bg-danger text-white"
                                    : "bg-success text-white"
                                }`}
                              >
                                {row.loan_status}
                              </span>
                            </td>
                            <td>{row.start_month}</td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={isEmployeeWise ? 7 : 10}
                          className="text-center text-danger fw-bold"
                        >
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanReport;
